import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Loader2, CheckCircle2, Lock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getUser, authHeaders, API } from "@/lib/auth";

const TERMS = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120];

const fmtEUR = (n) => n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function SimulatorPage() {
  const user = getUser();
  const [price, setPrice] = useState(25000);
  const [down, setDown] = useState(5000);
  const [months, setMonths] = useState(60);
  const [rate, setRate] = useState(7.5);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      if (r.data?.interest_rate != null) setRate(Number(r.data.interest_rate));
    }).catch(() => {});
  }, []);

  const result = useMemo(() => {
    const financed = Math.max(price - down, 0);
    const r = rate / 100 / 12;
    const monthly = financed <= 0 ? 0 : r <= 0 ? financed / months : (financed * r) / (1 - Math.pow(1 + r, -months));
    const total = monthly * months;
    return { financed, monthly, total, cost: total - financed };
  }, [price, down, months, rate]);

  const request = async () => {
    if (!user) { toast.error("Inicie sessão para solicitar financiamento."); return; }
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/client/processes`, {
        type: "financiamento",
        vehicle_price: price, down_payment: down, amount: result.financed, months, rate,
        notes: `Simulação: ${fmtEUR(result.monthly)}/mês`,
      }, { headers: authHeaders() });
      setSent(true);
      toast.success("Pedido de financiamento enviado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar o pedido.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="simulator-page">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-4 mb-3">
            <span className="w-12 h-12 flex items-center justify-center bg-[#0B1E36] text-white">
              <Calculator className="w-6 h-6" />
            </span>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0072C6]">Simulador de Financiamento</h1>
          </div>
          <p className="text-slate-600 max-w-2xl mb-4">Simule a sua prestação mensal e solicite financiamento. Prazos de 12 a 120 meses.</p>
          <p className="text-xs text-slate-400 max-w-2xl mb-10 leading-relaxed" data-testid="sim-disclaimer">
            <strong>Nota:</strong> Esta simulação é meramente indicativa e não constitui aprovação de crédito. As propostas finais dependem da análise da entidade financeira e das condições legalmente aplicáveis.
          </p>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Inputs */}
            <div className="lg:col-span-7 bg-[#F8FAFC] border border-slate-200 p-7 sm:p-10" data-testid="sim-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Preço do automóvel (€)</label>
                  <input data-testid="sim-price" type="number" min={0} step={500} value={price} onChange={(e) => setPrice(Number(e.target.value) || 0)}
                    className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Entrada inicial (€)</label>
                  <input data-testid="sim-down" type="number" min={0} step={500} value={down} onChange={(e) => setDown(Number(e.target.value) || 0)}
                    className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Prazo</label>
                  <select data-testid="sim-months" value={months} onChange={(e) => setMonths(Number(e.target.value))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                    {TERMS.map((m) => <option key={m} value={m}>{m} meses</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Taxa de juro indicativa — TAN (%)</label>
                  <div className="relative">
                    <input data-testid="sim-rate" type="text" value={`${rate.toLocaleString("pt-PT")} %`} readOnly disabled
                      className="w-full border border-slate-200 px-4 py-3 pr-10 text-sm bg-slate-50 text-slate-500 cursor-not-allowed" />
                    <Lock className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-400">Taxa de referência definida pela empresa.</p>
                </div>
              </div>
              <p className="mt-6 text-xs text-slate-400">Montante a financiar: <strong className="text-[#0072C6]">{fmtEUR(result.financed)}</strong> (preço − entrada)</p>
            </div>

            {/* Results */}
            <div className="lg:col-span-5">
              <div className="bg-[#0072C6] text-white p-8 sm:p-10" data-testid="sim-results">
                <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-300 mb-6">A Sua Simulação</h2>
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-slate-300">Valor da entrada</span><span data-testid="sim-out-down">{fmtEUR(down)}</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-slate-300">Capital financiado</span><span data-testid="sim-out-financed">{fmtEUR(result.financed)}</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-slate-300">Prazo</span><span data-testid="sim-out-months">{months} meses</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-slate-300">Prestação mensal estimada</span><span className="font-display text-2xl" data-testid="sim-out-monthly">{fmtEUR(result.monthly)}</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-slate-300">Montante total a pagar</span><span data-testid="sim-out-total">{fmtEUR(result.total)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-300">Custo estimado do financiamento</span><span data-testid="sim-out-cost">{fmtEUR(result.cost)}</span></div>
                </div>
                {sent ? (
                  <div className="mt-8 bg-emerald-500/20 border border-emerald-400/30 px-4 py-3 text-sm text-emerald-200 flex items-center gap-2" data-testid="sim-sent">
                    <CheckCircle2 className="w-4 h-4" /> Pedido enviado. Acompanhe em <Link to="/conta" className="underline">Minha Conta</Link>.
                  </div>
                ) : (
                  <button onClick={request} data-testid="sim-request" disabled={sending}
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-white text-[#0072C6] font-semibold px-8 py-4 text-sm hover:bg-slate-100 transition-colors disabled:opacity-60">
                    {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Solicitar Financiamento
                  </button>
                )}
                {!user && !sent && (
                  <p className="mt-3 text-center text-xs text-slate-400">
                    <Link to="/entrar" className="underline">Inicie sessão</Link> para enviar o pedido.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
